/**
 * Anon session + handoff for #580 six-turn public diagnostic (Lane A).
 * localStorage is durable; leadId rides along when capture already happened.
 * @module features/publicDiagnostic/session
 */
import { useSyncExternalStore } from 'react';
import { getItemWithTTL, setItemWithTTL, TTL } from '@/utils/localStorageWithTTL';
import { loadLeadRef } from '@/utils/leadRef';
import { findDiagnosticQuestion, parseDiagnosticPrefill } from '@/data/diagnosticBank';
import { buildAdaptiveResult, toAdaptiveTurnQuestion } from './adaptiveEngine';
import {
  publicDiagnosticClaimHandoff,
  publicDiagnosticNext,
  publicDiagnosticStart,
  publicDiagnosticSubmitTurn,
} from './api';
import { nextTurnId } from './turnMap';
import type {
  PublicDiagnosticHandoffPayload,
  PublicDiagnosticState,
  TurnId,
  TurnResult,
} from './types';

export const PUBLIC_DIAGNOSTIC_SESSION_KEY = 'swtpa_public_diag_v1';
/** Survives signup even if the claim callable is down. */
export const PUBLIC_DIAGNOSTIC_HANDOFF_KEY = 'swtpa_public_diag_handoff_v1';

type PrefetchStatus = 'pending' | 'ready' | 'miss';

interface PrefetchEntry {
  status: PrefetchStatus;
  data?: unknown;
  promise?: Promise<void>;
}

let memory: PublicDiagnosticState | null = null;
const prefetchCache = new Map<TurnId, PrefetchEntry>();
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((l) => l());
}

function emptyState(sessionId: string): PublicDiagnosticState {
  return {
    sessionId,
    currentTurn: 1,
    results: [],
    abilityEstimateStarted: false,
    simulationBegun: false,
    cardScheduled: false,
    pamAnswerReceived: false,
    degradedTurns: [],
  };
}

function isValidState(value: unknown): value is PublicDiagnosticState {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.sessionId === 'string' &&
    v.sessionId.length > 0 &&
    typeof v.currentTurn === 'number' &&
    v.currentTurn >= 1 &&
    v.currentTurn <= 6 &&
    Array.isArray(v.results)
  );
}

function persist(state: PublicDiagnosticState): void {
  memory = state;
  try {
    setItemWithTTL(PUBLIC_DIAGNOSTIC_SESSION_KEY, state, TTL.THREE_MONTHS);
  } catch {
    // private mode — keep in-memory only
  }
  emit();
}

function readStored(): PublicDiagnosticState | null {
  try {
    const raw = getItemWithTTL<unknown>(PUBLIC_DIAGNOSTIC_SESSION_KEY);
    return isValidState(raw) ? raw : null;
  } catch {
    return null;
  }
}

/** Ensure a session exists (mint via start callable / client UUID). */
export function ensureSession(): PublicDiagnosticState {
  if (memory && isValidState(memory)) return memory;
  const stored = readStored();
  if (stored) {
    memory = stored;
    return stored;
  }
  const fresh = emptyState(crypto.randomUUID());
  persist(fresh);
  return fresh;
}

function getSnapshot(): PublicDiagnosticState {
  return ensureSession();
}

function subscribe(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
}

/** Load/save anon state (localStorage + leadId when email already captured). */
export function usePublicDiagnosticSession(): PublicDiagnosticState {
  return useSyncExternalStore(subscribe, getSnapshot, () => emptyState('ssr'));
}

export function getPrefetchedTurn(turnId: TurnId): unknown | null {
  const entry = prefetchCache.get(turnId);
  return entry?.status === 'ready' ? (entry.data ?? null) : null;
}

export function isPrefetchPending(turnId: TurnId): boolean {
  return prefetchCache.get(turnId)?.status === 'pending';
}

/** Prefetch while visitor reads the current turn. Never blocks commit/UI advance. */
export async function prefetchTurn(next: TurnId): Promise<void> {
  const existing = prefetchCache.get(next);
  if (existing?.status === 'ready') return;
  if (existing?.status === 'pending' && existing.promise) return existing.promise;

  const state = ensureSession();
  let resolve!: () => void;
  const promise = new Promise<void>((r) => {
    resolve = r;
  });

  prefetchCache.set(next, { status: 'pending', promise });
  emit();

  try {
    const res = await publicDiagnosticNext(state.sessionId, next);
    prefetchCache.set(next, { status: 'ready', data: res.turn });
  } catch {
    // Local placeholder so the shell can still render the slot.
    prefetchCache.set(next, {
      status: 'ready',
      data: { turnId: next, kind: 'placeholder' },
    });
  } finally {
    emit();
    resolve();
  }

  return promise;
}

/** Seed turn 1 into the prefetch cache (and refresh sessionId from start if brand new). */
export async function startOrResumeSession(): Promise<PublicDiagnosticState> {
  const state = ensureSession();
  if (state.results.length === 0 && !getPrefetchedTurn(1)) {
    try {
      const started = await publicDiagnosticStart();
      if (state.sessionId !== started.sessionId && state.results.length === 0) {
        persist({ ...state, sessionId: started.sessionId });
      }
      prefetchCache.set(1, { status: 'ready', data: started.turn1 });
      emit();
    } catch {
      void prefetchTurn(1);
    }
  } else if (!getPrefetchedTurn(state.currentTurn)) {
    void prefetchTurn(state.currentTurn);
  }
  return ensureSession();
}

/** A skipped Ask Pam turn closes the flow but carries no answer, so it never rides the handoff. */
export function isSkippedAskPam(result: TurnResult): boolean {
  return result.kind === 'ask_pam' && result.answerMarkdown.trim() === '';
}

export async function commitTurn(result: TurnResult): Promise<void> {
  const state = ensureSession();
  const results = [...state.results.filter((r) => r.turnId !== result.turnId), result];
  const upcoming = nextTurnId(result.turnId);
  const next: PublicDiagnosticState = {
    ...state,
    results,
    currentTurn: upcoming ?? result.turnId,
    abilityEstimateStarted:
      state.abilityEstimateStarted ||
      result.kind === 'adaptive_question' ||
      result.kind === 'adaptive_followup' ||
      result.kind === 'timed_question',
    simulationBegun: state.simulationBegun || result.kind === 'simulation_beat',
    cardScheduled: state.cardScheduled || result.kind === 'flashcard',
    // A canned fallback or refusal is a tip, not an answer; only a live reply earns the end-screen line.
    pamAnswerReceived: state.pamAnswerReceived || (result.kind === 'ask_pam' && result.llmUsed && !isSkippedAskPam(result)),
  };
  // Advance UI immediately — prefetch is fire-and-forget.
  persist(next);
  void publicDiagnosticSubmitTurn(state.sessionId, result).catch(() => {});
  if (upcoming) {
    void prefetchTurn(upcoming);
  }
}

/** True exactly once per run: when its first turn is committed. A run already past turn 1 is marked without counting. */
export function claimStartTracking(): boolean {
  const state = ensureSession();
  if (state.startTracked || state.results.length === 0) return false;
  persist({ ...state, startTracked: true });
  return state.results.length === 1;
}

/** A /q email answer (`?prefill=<qid>:<letter>`) is turn 1 of a fresh run; a run already under way keeps its own turn 1. */
export async function creditPrefill(raw: string | null): Promise<boolean> {
  const prefill = parseDiagnosticPrefill(raw);
  const question = prefill && findDiagnosticQuestion(prefill.questionId);
  if (!prefill || !question || ensureSession().results.length > 0) return false;
  await commitTurn(
    buildAdaptiveResult({
      turnId: 1,
      kind: 'adaptive_question',
      question: toAdaptiveTurnQuestion(question),
      choiceId: prefill.letter,
    })
  );
  return true;
}

export function buildHandoffPayload(): PublicDiagnosticHandoffPayload {
  const state = ensureSession();
  const leadId = loadLeadRef() ?? undefined;
  return {
    version: 1,
    sessionId: state.sessionId,
    results: state.results.filter((r) => !isSkippedAskPam(r)),
    ...(leadId ? { leadId } : {}),
  };
}

interface StoredHandoff {
  uid: string;
  payload: PublicDiagnosticHandoffPayload;
  attachedAt: string;
  claimedAt?: string;
}

function writeHandoff(record: StoredHandoff): void {
  try {
    setItemWithTTL(PUBLIC_DIAGNOSTIC_HANDOFF_KEY, record, TTL.THREE_MONTHS);
  } catch {
    // private mode: the claim below is the only copy
  }
}

/** Drop the anonymous run so the next read mints a fresh one. The handoff key is left alone: it may belong to an account. */
export function resetPublicDiagnosticSession({ restarted = false } = {}): void {
  memory = null;
  prefetchCache.clear();
  if (restarted) {
    persist({ ...emptyState(crypto.randomUUID()), restarted: true });
    return;
  }
  try {
    localStorage.removeItem(PUBLIC_DIAGNOSTIC_SESSION_KEY);
  } catch {
    // private mode: memory reset above is enough for this load
  }
  emit();
}

/** The claimed run belongs to the account now; "/" starts a fresh diagnostic instead of its end screen. */
function retireClaimedSession(sessionId: string): void {
  if ((memory ?? readStored())?.sessionId !== sessionId) return;
  resetPublicDiagnosticSession();
}

async function claim(record: StoredHandoff): Promise<void> {
  try {
    // Resolves for a fresh claim and for "already claimed"; only transport/precondition failures throw.
    await publicDiagnosticClaimHandoff(record.uid, record.payload);
    writeHandoff({ ...record, claimedAt: new Date().toISOString() });
    retireClaimedSession(record.payload.sessionId);
  } catch {
    // unclaimed local copy stays; claimRunOnSignIn tries again on the next app load
  }
}

/** Merge handoff into account at signup; nothing lost (local copy + claim). Never throws. */
export async function attachHandoffOnSignup(
  uid: string,
  payload: PublicDiagnosticHandoffPayload
): Promise<void> {
  const record: StoredHandoff = { uid, payload, attachedAt: new Date().toISOString() };
  writeHandoff(record);
  await claim(record);
}

const signInClaimsThisLoad = new Set<string>();

/**
 * Any sign-in on this device (emailed link, /signin, Apple, Google) claims the
 * run it holds, once per account per load: a pending handoff for this account
 * (or with no uid), else a finished run. The backend allows one claim per
 * account, and "already claimed" resolves, so the local run is dropped either way.
 */
export async function claimRunOnSignIn(uid: string): Promise<void> {
  if (signInClaimsThisLoad.has(uid)) return;
  signInClaimsThisLoad.add(uid);
  let stored: StoredHandoff | null = null;
  try {
    stored = getItemWithTTL<StoredHandoff>(PUBLIC_DIAGNOSTIC_HANDOFF_KEY);
  } catch {
    return;
  }
  if (stored && !stored.claimedAt) {
    // Another account's failed signup claim: its run, not ours.
    if (stored.uid && stored.uid !== uid) return;
    await claim({ ...stored, uid });
    return;
  }
  if (stored?.claimedAt) retireClaimedSession(stored.payload.sessionId);
  // An unfinished run stays local: claiming it would spend the account's one claim and wipe her progress.
  const run = memory ?? readStored();
  if (run && run.results.length >= 6) await attachHandoffOnSignup(uid, buildHandoffPayload());
}


/** Test helper: wipe in-memory + prefetch cache (tests clear localStorage themselves). */
export function __resetPublicDiagnosticSessionForTests(): void {
  memory = null;
  signInClaimsThisLoad.clear();
  prefetchCache.clear();
  emit();
}
